import { formatCount, joinMeta } from './counts';

/**
 * A public form's VOLUME, as the charts renderer reads it (KPR-513; keepr-api
 * docs/SCHEMA.md § Volume, for both kinds). The API answers counts per bucket
 * — `{ granularity: 'hour' | 'day', from, to, buckets: [{ at, submitted,
 * accepted, rejected, botFailed, limited, reports }], totals }`, zero-filled, hours for
 * two days or fewer, else UTC days — for a permanent public form and for an
 * invite link alike.
 *
 * Rather than draw it with a chart of its own, this turns it into a chart RUN
 * RESULT (components/charts/chartResult.js, "The contract") so the platform's
 * one renderer draws it: the data palette, the marks, the tooltip, the aria
 * description, the table twin, printing and the phone layout all come with it
 * (standards §12). Rejected: a hand-built echarts option, which would have to
 * re-earn every one of those, and the first thing it would get wrong is the
 * dark-mode palette.
 *
 * The form is a LINE over time (standards §12 Forms: change over time), split
 * three ways — submitted, accepted, rejected. Not a stack: a submission is
 * counted when it arrives and its accept or reject when someone triages it,
 * often in a later bucket, so the three are not parts of one whole and a
 * stack would invent a total. The two cheap counters — bot checks that
 * failed and submissions the limits turned away — are said in words under
 * the chart (`volumeSentence`): the API's own warning is that a script can
 * move them at will, so they are volume to read, never a line to compare.
 *
 * No mark opens a list: a submission that was rejected no longer exists, and
 * the counts are tallies, not items.
 */

export const VOLUME_SERIES = Object.freeze([
	{ key: 'submitted', label: 'Submitted' },
	{ key: 'accepted', label: 'Accepted' },
	{ key: 'rejected', label: 'Rejected' },
]);

/**
 * KPR-514: "Report this form" reports, each a bot-check pass. A fourth line,
 * drawn only when the period holds one — most forms are never reported, and
 * a flat zero line on every chart would say nothing but take a colour. A
 * line beside the submissions rather than a sentence: a report is what can
 * pause a form, and a manager reading why it paused wants to see WHEN.
 */
export const REPORTS_SERIES = Object.freeze({ key: 'reports', label: 'Reported' });

/** The series this answer draws: the three, and Reported when anything was. */
export function volumeSeries(volume) {
	const buckets = Array.isArray(volume?.buckets) ? volume.buckets : [];
	const reported = whole(volume?.totals?.reports) > 0 || buckets.some(b => whole(b?.reports) > 0);
	return reported ? [...VOLUME_SERIES, REPORTS_SERIES] : [...VOLUME_SERIES];
}

/** The periods a manager may look back over, and the API's `days` for each. */
export const VOLUME_PERIODS = Object.freeze([
	{ value: 2, label: '48 hours' },
	{ value: 30, label: '30 days' },
	{ value: 90, label: '90 days' },
	{ value: 365, label: 'A year' },
]);

const whole = value => (Number.isFinite(Number(value)) && Number(value) > 0 ? Math.round(Number(value)) : 0);

/** The chart spec the result is drawn under: a count over time, split three ways, as a line. */
export function volumeSpec(granularity) {
	return {
		v: 1,
		measures: [{ key: 'm1', op: 'count' }],
		groupBy: { on: 'created', bucket: granularity === 'hour' ? 'hour' : 'day', blanks: 'show' },
		splitBy: { on: 'outcome' },
		show: { type: 'line' },
	};
}

/**
 * The volume answer → an ok run result, or null when the answer has no
 * buckets to draw. Times are the API's: UTC hours or UTC days (`tz: 'UTC'`,
 * so the chart says "Days in UTC" to a reader elsewhere — a day here is the
 * server's day, and the bucket would otherwise read as the viewer's).
 */
export function volumeResult(volume) {
	const buckets = Array.isArray(volume?.buckets) ? volume.buckets.filter(b => b && b.at) : [];
	if (!buckets.length) return null;
	const bucket = volume.granularity === 'hour' ? 'hour' : 'day';
	const sum = key => buckets.reduce((total, b) => total + whole(b[key]), 0);
	const totals = volume.totals && typeof volume.totals === 'object' ? volume.totals : {};
	const total = key => (Number.isFinite(Number(totals[key])) ? whole(totals[key]) : sum(key));
	const drawn = volumeSeries(volume);
	return {
		ok: true,
		tz: 'UTC',
		axis: { kind: 'time', bucket, keys: buckets.map(b => b.at) },
		series: drawn.map(series => ({ key: series.key, label: series.label })),
		measures: [{ key: 'm1', label: 'Submissions', type: 'number' }],
		values: [buckets.map(b => drawn.map(series => whole(b[series.key])))],
		totals: { m1: drawn.map(series => total(series.key)) },
		range: volume.from && volume.to ? { from: volume.from, to: volume.to } : undefined,
		doors: {
			axis: buckets.map(() => null),
			series: drawn.map(() => null),
			chart: null,
		},
		notes: [],
	};
}

/** The totals the line does not draw, in words — or null when there are none. */
export function volumeSentence(volume) {
	const totals = volume?.totals || {};
	const buckets = Array.isArray(volume?.buckets) ? volume.buckets : [];
	const count = key => (Number.isFinite(Number(totals[key])) ? whole(totals[key]) : buckets.reduce((t, b) => t + whole(b?.[key]), 0));
	const botFailed = count('botFailed');
	const limited = count('limited');
	const line = joinMeta(
		botFailed ? `${formatCount(botFailed, 'submission')} failed the bot check` : null,
		limited ? `${formatCount(limited, 'submission')} turned away by the limits` : null
	);
	return line ? `${line}.` : null;
}

/** Did anything arrive at all in the period? */
export function volumeIsQuiet(volume) {
	return !VOLUME_SERIES.some(series => (Array.isArray(volume?.buckets) ? volume.buckets : []).some(b => whole(b?.[series.key]) > 0));
}
